import React from "react";
import { MapPin, Phone, Mail, Clock, Languages, Tag, CreditCard, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";

const TherapistNearMe: React.FC = () => {
    
    const navigate = useNavigate();

    return (
        <div className="flex flex-col min-h-screen bg-white">
            {/* Hero Section */}
            <section className="bg-gradient-to-r from-teal-600 to-teal-800 text-white py-36 px-4 md:px-8">
                <div className="max-w-6xl mx-auto">
                    <div className="flex flex-col md:flex-row items-center justify-between">
                        <div className="md:w-1/2 mb-8 md:mb-0">
                            <h1 className="text-4xl md:text-5xl font-bold mb-4 mt-4">
                                Therapist Near Me 
                            </h1>
                            <p className="text-lg mb-8">
                                Expert mental health professionals dedicated to your well-being. Discover compassionate care with Greh Pravesh Therapy.
                            </p>
                            <button
                            onClick={()=>navigate("/book-appointment")}
                            className="bg-white text-teal-700 px-6 py-3 rounded-lg font-medium hover:bg-teal-100 transition duration-300">
                                Book an Appointment
                            </button>
                        </div>
                        <div className="md:w-1/2">
                            <img
                                 src="/images/wt1.jpg"
                                alt="grehpravesh therapy best therapist near me"
                                className="rounded-lg shadow-xl w-full"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* Main Content Section */}
            <section className="py-12 md:px-8 max-w-6xl mx-auto">
                <div className="bg-white rounded-lg shadow-md p-6 md:p-8 mb-10">
                    <h2 className="text-2xl md:text-3xl font-semibold text-teal-800 mb-6">
                        Looking for a Therapist Near Your Location?
                    </h2>
                    <p className="mb-6 text-gray-700">
                        Explore our platform to browse through our experienced team of therapists and Therapists based in Delhi, and use our hassle-free online booking system for appointments. If there aren't many therapists listed in Delhi yet, don't worry! Many of our listed therapists offer online counselling as an option.
                    </p>
                    <p className="mb-6 text-gray-700">
                        Online private therapy services, couples counselling, or coaching, can be found by using the filter for online therapists in our team page. With our filtering/sorting system you can match with an Ideal therapist.
                    </p>
                    <div className="inline-block bg-teal-100 text-teal-800 px-3 py-1 rounded-full text-sm font-medium mb-6">
                        Best Therapist Near Me 
                    </div>
                </div>

                {/* How to find section */}
                <div className="bg-sage bg-opacity-20 rounded-lg shadow-md p-6 md:p-8 mb-10">
                    <h2 className="text-2xl md:text-3xl font-semibold text-teal-800 mb-6">
                        How to find a Therapist Near Your Location?
                    </h2>
                    <p className="mb-6 text-gray-700">
                        The philosophy of Greh Pravesh therapy proudly advocates both accessibility and diversity at its heart, and we understand that there are many reasons why you might feel more comfortable with a Therapist who shares similar demographics. If you have specific preferences for your Therapist, you can use our built-in therapy search filters to find the right fit for you.
                    </p>
                    <p className="mb-6 text-gray-700">
                        Search for English-speaking therapists in Delhi by filtering by language in the search tool here in our directory of licensed professionals and discover other filter options such as:
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                        <div className="flex items-center p-4 bg-white rounded-lg shadow">
                            <Tag className="text-purple h-5 w-5 mr-2" />
                            <span>Concerns</span>
                        </div>
                        <div className="flex items-center p-4 bg-white rounded-lg shadow">
                            <CreditCard className="text-purple h-5 w-5 mr-2" />
                            <span>Price Range</span>
                        </div>
                        <div className="flex items-center p-4 bg-white rounded-lg shadow">
                            <Users className="text-purple h-5 w-5 mr-2" />
                            <span>Experience</span>
                        </div>
                        <div className="flex items-center p-4 bg-white rounded-lg shadow">
                            <Clock className="text-purple h-5 w-5 mr-2" />
                            <span>Availability</span>
                        </div>
                        <div className="flex items-center p-4 bg-white rounded-lg shadow">
                            <Languages className="text-purple h-5 w-5 mr-2" />
                            <span>Languages</span>
                        </div>
                    </div>

                    <div className="inline-block bg-purple text-white px-3 py-1 rounded-full text-sm font-medium">
                        Pro Bono Services Available
                    </div>
                </div>

                {/* Services Section */}
                <div className="mb-10">
                    <h2 className="text-2xl md:text-3xl font-semibold text-teal-800 mb-6 text-center">
                        Our Therapy Services
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 px-4">
                        <div className="bg-white rounded-lg shadow-md overflow-hidden">
                            <div className="h-48 bg-earthybeige bg-opacity-20">
                                <img
                                    src="/images/trauma-1.png"
                                    alt="grehpravesh therapy best therapist near me"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <div className="p-6">
                                <h3 className="text-xl font-semibold mb-3 text-teal-700">Mental Health</h3>
                                <p className="text-gray-600">Depression, Anxiety, Burnout, Overthinking, Stress, ADHD, Sleep Issues, Fears, Grief, Phobias, Neurodivergence, Adjustment, Trauma</p>
                            </div>
                        </div>

                        <div className="bg-white rounded-lg shadow-md overflow-hidden">
                            <div className="h-48 bg-earthybeige bg-opacity-20">
                                <img
                                    src="/images/relationship1.png"
                                    alt="grehpravesh therapy best therapist near me"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <div className="p-6">
                                <h3 className="text-xl font-semibold mb-3 text-teal-700">Relationship Issues</h3>
                                <p className="text-gray-600">Family Conflicts, Boundary Settings, Interpersonal Concerns, Family Issues, Romantic Relationship, Attachment Difficulty, Friendships, Couples Therapy</p>
                            </div>
                        </div>

                        <div className="bg-white rounded-lg shadow-md overflow-hidden">
                            <div className="h-48 bg-earthybeige bg-opacity-20">
                                <img
                                    src="/images/group-therapy-1.jpg"
                                    alt="grehpravesh therapy best therapist near me"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <div className="p-6">
                                <h3 className="text-xl font-semibold mb-3 text-teal-700">Self Development</h3>
                                <p className="text-gray-600">Self-Esteem, Personal Growth, Identity Exploration, Perfectionism, Life Transitions, Self-Exploration, Body Image, Growth and Healing, Imposter Syndrome</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Locations Section */}
                <div className="bg-golden-yellow bg-opacity-10 rounded-lg shadow-md p-6 md:p-8 mb-10">
                    <h2 className="text-2xl md:text-3xl font-semibold text-teal-800 mb-6">
                        Visit Us in Your Neighborhood
                    </h2>
                    <p className="mb-6 text-gray-700">
                        All our therapists are available daily from 10 AM to 9 PM, making it easier to fit therapy into your busy life. Moreover, you can coordinate timing according to your's and your therapist's sechdule.
                    </p>

                    <div className="mb-8">
                        <div className="bg-white p-6 rounded-lg shadow-md mb-6">
                            <h3 className="text-xl font-semibold mb-2 text-teal-700">West Patel Nagar - Main Center</h3>
                            <p className="flex items-start mb-2">
                                <MapPin className="text-golden-yellow h-5 w-5 mr-2 mt-1 flex-shrink-0" />
                                <span>4/20, WEST PATEL NAGAR, Delhi-110008</span>
                            </p>
                            <ul className="ml-7 text-gray-600 list-disc">
                                <li>Our flagship center with private, comfortable therapy rooms</li>
                                <li>Easy access from Patel Nagar Metro Station</li>
                                <li>Wheelchair accessible facility</li>
                                <li>Special family therapy rooms available</li>
                                <li>Free parking available nearby</li>
                            </ul>
                        </div>

                                               <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                            <div className="bg-white p-4 rounded-lg shadow-md flex items-center justify-center">
                                <span className="text-teal-600">Karol Bagh</span>
                            </div>
                            <div className="bg-white p-4 rounded-lg shadow-md flex items-center justify-center">
                                <span className="text-teal-600">Lajpat Nagar</span>
                            </div>
                            <div className="bg-white p-4 rounded-lg shadow-md flex items-center justify-center">
                                <span className="text-teal-600">Kamla Nagar</span>
                            </div>
                            <div className="bg-white p-4 rounded-lg shadow-md flex items-center justify-center">
                                <span className="text-teal-600">Connaught Place</span>
                            </div>
                            <div className="bg-white p-4 rounded-lg shadow-md flex items-center justify-center">
                                <span className="text-teal-600">Pitampura</span>
                            </div>
                            <div className="bg-white p-4 rounded-lg shadow-md flex items-center justify-center">
                                <span className="text-teal-600">Shastri Nagar</span>
                            </div>
                            <div className="bg-white p-4 rounded-lg shadow-md flex items-center justify-center">
                                <span className="text-teal-600">Pusa Institute</span>
                            </div>
                            <div className="bg-white p-4 rounded-lg shadow-md flex items-center justify-center">
                                <span className="text-teal-600">Hauz Khas</span>
                            </div>
                            <div className="bg-white p-4 rounded-lg shadow-md flex items-center justify-center">
                                <span className="text-teal-600">Saket</span>
                            </div>
                            <div className="bg-white p-4 rounded-lg shadow-md flex items-center justify-center">
                                <span className="text-teal-600">Malviya Nagar</span>
                            </div>
                            <div className="bg-white p-4 rounded-lg shadow-md flex items-center justify-center">
                                <span className="text-teal-600">Vasant Kunj</span>
                            </div>

                            <div className="bg-white p-4 rounded-lg shadow-md flex items-center justify-center">
                                <span className="text-teal-600">Gurgaon</span>
                            </div>


                        </div>
                    </div>

                    <div className="inline-block bg-teal-100 text-teal-800 px-3 py-1 rounded-full text-sm font-medium">
                        Best Therapist Near Me 
                    </div>
                </div>

                {/* Pricing Section */}
                <div className="bg-white rounded-lg shadow-md p-6 md:p-8 mb-10">
                    <h2 className="text-2xl md:text-3xl font-semibold text-teal-800 mb-6">
                        Affordable Help That Works
                    </h2>
                    <p className="mb-6 text-gray-700">
                        We believe good mental health support should be available to everyone. As India's first mental health practice prioritizing affordability in a sustainable way, we offer:
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        <div className="bg-sage bg-opacity-10 p-4 rounded-lg">
                            <h3 className="font-semibold mb-2">Regular sessions</h3>
                            <p>₹1000-2000 per session</p>
                        </div>
                        <div className="bg-sage bg-opacity-10 p-4 rounded-lg">
                            <h3 className="font-semibold mb-2">Student discount</h3>
                            <p>Special rates starting at ₹800 for college students</p>
                        </div>
                        <div className="bg-sage bg-opacity-10 p-4 rounded-lg">
                            <h3 className="font-semibold mb-2">Unemployed individuals</h3>
                            <p>Reduced fees from ₹700 based on circumstances</p>
                        </div>
                        <div className="bg-sage bg-opacity-10 p-4 rounded-lg">
                            <h3 className="font-semibold mb-2">Sliding scale options</h3>
                            <p>Pay what you can afford based on your income</p>
                        </div>
                    </div>

                    <p className="text-gray-700">
                        Our flexible payment options mean that financial constraints need not be a barrier to seeking help. We're committed to making mental healthcare accessible without compromising on quality.
                    </p>
                </div>

                {/* Approach Section */}
                <div className="bg-purple bg-opacity-10 rounded-lg shadow-md p-6 md:p-8 mb-10">
                    <h2 className="text-2xl md:text-3xl font-semibold text-teal-800 mb-6">
                        Our Approach to Healing
                    </h2>
                    <p className="mb-6 text-gray-700">
                        Our therapists create a safe space tailored to your unique needs. At each of our Delhi locations, you'll find specialized approaches:
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 mb-8">
                        <div className="flex items-start">
                            <div className="h-6 w-6 rounded-full bg-purple text-white flex items-center justify-center font-bold mr-3 mt-1">1</div>
                            <div>
                                <h3 className="font-semibold mb-1">Cognitive Behavioral Therapy (CBT)</h3>
                                <p className="text-gray-600 text-sm">Helps change unhelpful thought patterns</p>
                            </div>
                        </div>
                        <div className="flex items-start">
                            <div className="h-6 w-6 rounded-full bg-purple text-white flex items-center justify-center font-bold mr-3 mt-1">2</div>
                            <div>
                                <h3 className="font-semibold mb-1">Trauma-Focused Therapy</h3>
                                <p className="text-gray-600 text-sm">Specially designed for past difficult experiences</p>
                            </div>
                        </div>
                        <div className="flex items-start">
                            <div className="h-6 w-6 rounded-full bg-purple text-white flex items-center justify-center font-bold mr-3 mt-1">3</div>
                            <div>
                                <h3 className="font-semibold mb-1">Mindfulness Practices</h3>
                                <p className="text-gray-600 text-sm">Learn to stay present and reduce stress</p>
                            </div>
                        </div>
                        <div className="flex items-start">
                            <div className="h-6 w-6 rounded-full bg-purple text-white flex items-center justify-center font-bold mr-3 mt-1">4</div>
                            <div>
                                <h3 className="font-semibold mb-1">Solution-Focused Brief Therapy</h3>
                                <p className="text-gray-600 text-sm">Practical tools for immediate concerns</p>
                            </div>
                        </div>
                        <div className="flex items-start">
                            <div className="h-6 w-6 rounded-full bg-purple text-white flex items-center justify-center font-bold mr-3 mt-1">5</div>
                            <div>
                                <h3 className="font-semibold mb-1">Family Systems Therapy</h3>
                                <p className="text-gray-600 text-sm">Heal relationships with loved ones</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white p-6 rounded-lg shadow-md">
                        <h3 className="text-lg font-semibold mb-4 text-teal-700">No matter which location you choose, our therapists create a space where you can:</h3>
                        <ul className="space-y-2 text-gray-700">
                            <li className="flex items-center">
                                <div className="h-2 w-2 rounded-full bg-golden-yellow mr-3"></div>
                                Talk openly without judgment
                            </li>
                            <li className="flex items-center">
                                <div className="h-2 w-2 rounded-full bg-golden-yellow mr-3"></div>
                                Learn tools to manage tough feelings
                            </li>
                            <li className="flex items-center">
                                <div className="h-2 w-2 rounded-full bg-golden-yellow mr-3"></div>
                                Build stronger relationships
                            </li>
                            <li className="flex items-center">
                                <div className="h-2 w-2 rounded-full bg-golden-yellow mr-3"></div>
                                Find your own path forward
                            </li>
                            <li className="flex items-center">
                                <div className="h-2 w-2 rounded-full bg-golden-yellow mr-3"></div>
                                Discover your inner strength
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Booking Section */}
                <div className="bg-earthybeige bg-opacity-10 rounded-lg shadow-md p-6 md:p-8 mb-10">
                    <h2 className="text-2xl md:text-3xl font-semibold text-teal-800 mb-6">
                        Take Your First Step Towards Better Mental Well-being
                    </h2>
                    <p className="mb-6 text-gray-700">
                        Through our digital platform, you can book your appointment any time for online, and as well as offline sessions. To book appointment, please follow the below written steps:
                    </p>

                    <div className="bg-white p-6 rounded-lg shadow-md mb-6">
                        <ol className="space-y-4">
                            <li className="flex">
                                <span className="bg-earthybeige text-white h-6 w-6 rounded-full flex items-center justify-center mr-3">1</span>
                                <span>Visit: <a href="https://grehpraveshtherapy.com/book-appointment" className="text-teal-700 underline">https://grehpraveshtherapy.com/book-appointment</a></span>
                            </li>
                            <li className="flex">
                                <span className="bg-earthybeige text-white h-6 w-6 rounded-full flex items-center justify-center mr-3">2</span>
                                <span>Fill the form.</span>
                            </li>
                            <li className="flex">
                                <span className="bg-earthybeige text-white h-6 w-6 rounded-full flex items-center justify-center mr-3">3</span>
                                <span>Select preferred therapist (if you've not explored our team then visit: <a href="https://grehpraveshtherapy.com/" className="text-teal-700 underline">https://grehpraveshtherapy.com/</a>)</span>
                            </li>
                        </ol>
                    </div>

                    <p className="mb-6 text-gray-700">
                        For booking therapy sessions with Grehpravesh Therapy you're not bound to choose any location. You can sign up for therapy from the comfort of your home.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                        <div className="bg-white p-4 rounded-lg shadow text-center">
                            <h3 className="font-semibold mb-2 text-teal-700">Individual Therapy</h3>
                            <p className="text-sm text-gray-600">Personalized one-on-one support</p>
                        </div>
                        <div className="bg-white p-4 rounded-lg shadow text-center">
                            <h3 className="font-semibold mb-2 text-teal-700">Couples Therapy</h3>
                            <p className="text-sm text-gray-600">Strengthen your relationship</p>
                        </div>
                        <div className="bg-white p-4 rounded-lg shadow text-center">
                            <h3 className="font-semibold mb-2 text-teal-700">Family Therapy</h3>
                            <p className="text-sm text-gray-600">Heal family dynamics</p>
                        </div>
                    </div>

                    <p className="text-gray-700">
                        When you contact us, we'll ask a few simple questions to match you with the right therapist and location for your specific needs.
                    </p>
                </div>

                {/* Why Choose Us Section */}
                <div className="bg-white rounded-lg shadow-md p-6 md:p-8 mb-10">
                    <h2 className="text-2xl md:text-3xl font-semibold text-teal-800 mb-6">
                        Why Choose Greh Pravesh?
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                        <div className="flex items-start">
                            <div className="bg-golden-yellow h-8 w-8 rounded-full flex items-center justify-center text-white font-bold mr-3">✓</div>
                            <div>
                                <h3 className="font-semibold">We're truly local</h3>
                                <p className="text-gray-600 text-sm">With therapists from different cities of India</p>
                            </div>
                        </div>
                        <div className="flex items-start">
                            <div className="bg-golden-yellow h-8 w-8 rounded-full flex items-center justify-center text-white font-bold mr-3">✓</div>
                            <div>
                                <h3 className="font-semibold">Flexible timing</h3>
                                <p className="text-gray-600 text-sm">Evening and weekend appointments available</p>
                            </div>
                        </div>
                        <div className="flex items-start">
                            <div className="bg-golden-yellow h-8 w-8 rounded-full flex items-center justify-center text-white font-bold mr-3">✓</div>
                            <div>
                                <h3 className="font-semibold">Affordable options</h3>
                                <p className="text-gray-600 text-sm">India's first mental health practice prioritizing affordability</p>
                            </div>
                        </div>
                        <div className="flex items-start">
                            <div className="bg-golden-yellow h-8 w-8 rounded-full flex items-center justify-center text-white font-bold mr-3">✓</div>
                            <div>
                                <h3 className="font-semibold">Experienced team</h3>
                                <p className="text-gray-600 text-sm">Therapists trained at top institutions of India and abroad</p>
                            </div>
                        </div>
                        <div className="flex items-start">
                            <div className="bg-golden-yellow h-8 w-8 rounded-full flex items-center justify-center text-white font-bold mr-3">✓</div>
                            <div>
                                <h3 className="font-semibold">Personalized care</h3>
                                <p className="text-gray-600 text-sm">We see you as a whole person, not just symptoms</p>
                            </div>
                        </div>
                        <div className="flex items-start">
                            <div className="bg-golden-yellow h-8 w-8 rounded-full flex items-center justify-center text-white font-bold mr-3">✓</div>
                            <div>
                                <h3 className="font-semibold">Cultural understanding</h3>
                                <p className="text-gray-600 text-sm">Our therapists understand Indian family dynamics</p>
                            </div>
                        </div>
                        <div className="flex items-start">
                            <div className="bg-golden-yellow h-8 w-8 rounded-full flex items-center justify-center text-white font-bold mr-3">✓</div>
                            <div>
                                <h3 className="font-semibold">Digital options</h3>
                                <p className="text-gray-600 text-sm">Online therapy available when you can't visit in person</p>
                            </div>
                        </div>
                        <div className="flex items-start">
                            <div className="bg-golden-yellow h-8 w-8 rounded-full flex items-center justify-center text-white font-bold mr-3">✓</div>
                            <div>
                                <h3 className="font-semibold">Holistic approach</h3>
                                <p className="text-gray-600 text-sm">We consider all aspects of your wellbeing</p>
                            </div>
                        </div>
                    </div>

                    <div className="inline-block bg-teal-100 text-teal-800 px-3 py-1 rounded-full text-sm font-medium">
                        Best Therapist Near Me 
                    </div>
                </div>

                {/* Final CTA Section */}
                <div className="bg-purple bg-opacity-10 rounded-lg shadow-md p-6 md:p-8 text-center">
                    <h2 className="text-2xl md:text-3xl font-semibold text-teal-800 mb-6">
                        You're Not Alone
                    </h2>
                    <p className="mb-6 text-gray-700 max-w-3xl mx-auto">
                        Finding a Therapist near you in Delhi shouldn't be difficult. At Greh Pravesh, we've created our main center in the post locality of West Patel Nagar.
                    </p>
                    <p className="mb-8 text-gray-700 max-w-3xl mx-auto">
                        Taking care of your mental health is just as important as caring for your physical health. Whether you're going through a rough patch or dealing with long-term challenges, reaching out is the first step toward feeling better.
                    </p>

                    <div className="bg-white p-6 rounded-lg shadow-md inline-block">
                        <div className="flex flex-col md:flex-row items-center justify-center gap-6">
                            <div className="flex items-center">
                                <Phone className="text-purple h-5 w-5 mr-2" />
                                <span>+91 8591049845</span>
                            </div>
                            <div className="flex items-center">
                                <Mail className="text-purple h-5 w-5 mr-2" />
                                <span>grehpraveshtherapy@gmail.com</span>
                            </div>
                            <div className="flex items-center">
                                <Clock className="text-purple h-5 w-5 mr-2" />
                                <span>Mon-Sun: 10 AM - 9 PM</span>
                            </div>
                        </div>
                    </div>

                    <div className="mt-8">
                        <p className="text-lg font-medium text-teal-800">
                            Greh Pravesh Therapy - Creating compassionate spaces for genuine healing and growth of your mental well-being.
                        </p>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default TherapistNearMe;